"use client";

import React, { useEffect, useState } from 'react';

interface TocItem {
  id: string;
  title: string;
  level: number;
}

interface OnThisPageProps {
  className?: string;
}

export default function OnThisPage({ className = '' }: OnThisPageProps) {
  const [tocItems, setTocItems] = useState<TocItem[]>([]);
  const [activeId, setActiveId] = useState<string>('');

  useEffect(() => {
    // Extract headings from the page
    const headings = document.querySelectorAll('h1, h2, h3, h4');
    const items: TocItem[] = [];

    headings.forEach((heading) => {
      if (heading.id) {
        const level = parseInt(heading.tagName.charAt(1));
        // Only include h2 and h3 for cleaner TOC
        if (level >= 2 && level <= 3) {
          items.push({
            id: heading.id,
            title: heading.textContent || '',
            level: level
          });
        }
      }
    });

    setTocItems(items);

    // Set up intersection observer for active section tracking
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      {
        rootMargin: '-130px 0% -35% 0%', // Account for sticky header height
        threshold: 0
      }
    );

    headings.forEach((heading) => {
      if (heading.id) observer.observe(heading);
    });

    return () => observer.disconnect();
  }, []);

  if (tocItems.length === 0) {
    return null;
  }

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const headerOffset = 130; // Account for sticky header height (121px) + some padding
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <>
      <div className="text-gray-1000 mb-1 mt-[7px] text-sm font-medium">On this page</div>
      <div className="relative" data-docs-table-of-contents="">
        {/* <ul className="styled-scrollbar max-h-[70vh] space-y-2.5 overflow-y-auto py-2 text-sm"> */}
        <ul className="styled-scrollbar max-h-[70vh] space-y-2.5 py-2 text-sm">
          {tocItems.map((item, index) => (
            <li key={item.id}>
              <button
                onClick={() => scrollToSection(item.id)}
                className={`hover:text-gray-1000 block leading-[1.6] transition-colors duration-200 w-full text-left ${
                  activeId === item.id 
                    ? 'text-gray-1000 font-medium' 
                    : 'text-gray-900'
                } ${
                  item.level === 3 ? 'ml-4' : ''
                }`}
              >
                {item.title}
              </button>
            </li>
          ))}
        </ul>
      </div>
      
      {/* Footer actions similar to Next.js docs */}
      <div className="mt-3 space-y-2 border-t border-gray-200 pt-5 text-sm text-gray-900 dark:border-gray-300">
        <button 
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="hover:text-gray-1000 flex items-center gap-x-1.5 text-sm text-gray-900 transition-opacity"
          type="button"
        >
          Scroll to top 
          <svg data-testid="geist-icon" height="16" strokeLinejoin="round" viewBox="0 0 16 16" width="16" style={{color: 'currentcolor'}}>
            <path fillRule="evenodd" clipRule="evenodd" d="M7.25 10.75V11.5H8.75V10.75V6.56066L10.2197 8.03033L10.75 8.56066L11.8107 7.5L11.2803 6.96967L8.53033 4.21967C8.23744 3.92678 7.76256 3.92678 7.46967 4.21967L4.71967 6.96967L4.18934 7.5L5.25 8.56066L5.78033 8.03033L7.25 6.56066V10.75ZM14.5 8C14.5 11.5899 11.5899 14.5 8 14.5C4.41015 14.5 1.5 11.5899 1.5 8C1.5 4.41015 4.41015 1.5 8 1.5C11.5899 1.5 14.5 4.41015 14.5 8ZM8 16C12.4183 16 16 12.4183 16 8C16 3.58172 12.4183 0 8 0C3.58172 0 0 3.58172 0 8C0 12.4183 3.58172 16 8 16Z" fill="currentColor"></path>
          </svg>
        </button>
      </div>
    </>
  );
} 